import { forwardRef, useCallback, useMemo, useRef, JSX } from 'react';
import type { Ref } from 'react';
import type {
  FieldPath,
  FieldValues,
  UseControllerReturn,
} from 'react-hook-form';

import {
  FileUpload as BasicFileUpload,
  type FileUploadOptions,
  type FileUploadRef,
} from '../index';

export type FileUploadUploadResult = {
  documentId: string;
  fileName?: string;
};

export type FileUploadApi = (
  request: { document: File },
  options?: { onProgress?: (progress: number) => void; signal?: AbortSignal }
) => Promise<FileUploadUploadResult>;

export type DocumentValue = {
  documentId: string;
  fileName: string;
  fileUrl?: string;
};

function documentValueToExistingFile(doc: DocumentValue) {
  return {
    id: doc.documentId,
    fileName: doc.fileName,
    fileUrl: doc.fileUrl ?? '',
  };
}

interface Props<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> extends UseControllerReturn<TFieldValues, TName> {
  title: string;
  description?: string;
  options?: FileUploadOptions;
  onUpload: FileUploadApi;
  onFileRemove?: (documentId: string) => void;
}

function FileUploadInner<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(props: Props<TFieldValues, TName>, ref: Ref<FileUploadRef>) {
  const {
    field: { value, onChange, onBlur },
    fieldState,
    formState: _formState,
    onUpload: onUploadProp,
    onFileRemove: onFileRemoveProp,
    options = {},
    ...uploadProps
  } = props;

  const multiple = options.multiple ?? false;
  const current = value as DocumentValue | DocumentValue[] | null | undefined;
  const valueRef = useRef<DocumentValue | DocumentValue[] | null>(
    multiple ? [] : null
  );
  valueRef.current = multiple
    ? Array.isArray(current)
      ? current
      : []
    : current && typeof current === 'object' && 'documentId' in current
      ? current
      : null;

  const existingFilesFromValue = useMemo(() => {
    if (multiple && Array.isArray(current)) {
      return current.map(documentValueToExistingFile);
    }
    if (
      !multiple &&
      current &&
      typeof current === 'object' &&
      'documentId' in current
    ) {
      return [documentValueToExistingFile(current as DocumentValue)];
    }
    return [];
  }, [current, multiple]);

  const errorMessage = fieldState.error?.message;

  const handleUpload = useCallback(
    async (
      file: { fileUrl?: string; file: File },
      onProgress: (progress: number) => void,
      signal?: AbortSignal
    ) => {
      const result = await onUploadProp(
        { document: file.file },
        { onProgress, signal }
      );

      const documentValue: DocumentValue = {
        documentId: result.documentId,
        fileName: file.file.name ?? '',
        fileUrl: file.fileUrl,
      };

      if (multiple) {
        const list = Array.isArray(valueRef.current) ? valueRef.current : [];
        const next = [...list, documentValue];
        valueRef.current = next;
        onChange(next);
      } else {
        valueRef.current = documentValue;
        onChange(documentValue);
      }

      onBlur();

      return { documentId: result.documentId, fileName: result.fileName };
    },
    [onUploadProp, multiple, onChange, onBlur]
  );

  const handleFileRemove = useCallback(
    (documentId: string) => {
      if (multiple) {
        const list = Array.isArray(valueRef.current) ? valueRef.current : [];
        const next = list.filter((doc) => doc.documentId !== documentId);
        valueRef.current = next;
        onChange(next);
      } else {
        valueRef.current = null;
        onChange(null);
      }
      onBlur();
      onFileRemoveProp?.(documentId);
    },
    [multiple, onChange, onBlur, onFileRemoveProp]
  );

  return (
    <div className="flex flex-col gap-1">
      <BasicFileUpload
        ref={ref}
        {...uploadProps}
        options={options}
        preloadedFiles={existingFilesFromValue}
        onUpload={handleUpload}
        onFileRemove={handleFileRemove}
      />
      {errorMessage && (
        <span className="text-sm text-red-600">{errorMessage}</span>
      )}
    </div>
  );
}

export const FileUpload = forwardRef(FileUploadInner) as <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: Props<TFieldValues, TName> & { ref?: Ref<FileUploadRef> }
) => JSX.Element;
